import ArticleIcon from '@mui/icons-material/Article';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { LinearProgress, MenuItem, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { capitalCase } from 'change-case';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import Iconify from 'src/components/Iconify';
import { TableMoreMenu } from 'src/components/table';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { fPercent } from 'src/utils/formatNumber';
import { getAge } from 'src/utils/formatTime';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import { PrintableCarePlan } from 'src/careflow/care-coordination/case/printable-care-plan/PrintableCarePlan';
import Edit from './Edit';
import CustomLink from 'src/careflow/common/CustomLink';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { WorkflowService } from 'src/careflow/ph-core/workflow/api/WorkflowService';

export default function Row({ row, handleRefresh }: any) {
  const [stages, setStages] = useState<any>([]);
  const [openMenu, setOpenMenuActions] = useState<HTMLElement | null>(null);
  const [openModalEdit, setOpenModalEdit] = useState(false);
    const { i18n } = useLocales();

  useEffect(() => {
    if(row?.scope)
      fetchWorkFlow(row?.scope);
  }, [row]);

  const fetchWorkFlow = async (workflowType: string | undefined) => {
    const getWorkflow = await WorkflowService.getWorkflowByCode(workflowType);
    setStages(getWorkflow?.stages);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOpenMenuActions(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpenMenuActions(null);
  };

  const handleEdit = () => {
    handleCloseMenu();
    setOpenModalEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenModalEdit(false);
  };

  return (
    <TableRow>
      <TableCell>
        <Typography noWrap variant="body2">
          <CustomLink 
            to={`${PATH_DASHBOARD.crs.case}/${row?.id}`}
          >
            {row?.internalNumber}
          </CustomLink>
        </Typography>
        <Edit
          data={row}
          open={openModalEdit}
          onClose={handleCloseEdit}
          handleRefresh={handleRefresh}
          i18n={i18n}
        />
      </TableCell>
      <TableCell>
        <Typography>
            {row?.createdOnFormatted &&
              format(new Date(row?.createdOnFormatted), 'MMM dd, yyyy hh:mm')}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography>{row?.birthDate && `${getAge(row?.birthDate)} years`}</Typography>
        </TableCell>
        <TableCell>
          <Typography sx={{ textTransform: 'capitalize' }}>{row?.statusIndexed}</Typography>
        </TableCell>
        <TableCell>
          <Typography>{row?.status && capitalCase(row.status.replace('.', ' '))}</Typography>
        </TableCell>
        <TableCell>
          <Typography textTransform="capitalize">
            {row?.owner && row.owner.split('@')[0].replace('.', ' ')}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography>{row?.scope}</Typography>
        </TableCell>
        <TableCell>
          <ProgressBar row={row?.stepOrder} stepLength={stages?.length} />
        </TableCell>
        {row?.scope === 'REFERRALS_WORKFLOW' &&
          checkAclValidation({ acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.EDIT] }) && (
            <TableCell align="right">
              <TableMoreMenu
                open={openMenu}
                onOpen={handleOpenMenu}
                onClose={handleCloseMenu}
                actions={[
                  <MenuItem key="edit" onClick={handleEdit}>
                    <Iconify icon={'mdi:pencil'} />
                    Edit
                  </MenuItem>,
                  <a
                    key="detail"
                    href={`${PATH_DASHBOARD.crs.referral}/${row?.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    <MenuItem sx={{ color: 'black' }}>
                      <OpenInNewIcon />
                      Detail
                    </MenuItem>
                  </a>,
                ]}
              />
            </TableCell>
          )}

        {['CASES_WORKFLOW','CARE_PLAN'].includes(row?.scope) && (
          <TableCell align="right">
            <TableMoreMenu
              width={260}
              open={openMenu}
              onOpen={handleOpenMenu}
              onClose={handleCloseMenu}
              actions={[
                checkAclValidation({ acls: [crsAcls.CRS.PATIENT.CASES.EDIT] }) && (
                  <MenuItem key="edit" onClick={handleEdit}>
                    <Iconify icon={'mdi:pencil'} />
                    Edit
                  </MenuItem>
                ),
                checkAclValidation({ acls: [crsAcls.CRS.CASE.VIEW] }) && (
                  <a
                    key="detail"
                    href={`${PATH_DASHBOARD.crs.case}/${row?.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    <MenuItem sx={{ color: 'black' }}>
                      <OpenInNewIcon />
                      Detail
                    </MenuItem>
                  </a>
                ),
                <PrintableCarePlan
                  key={`print-${row?.id}`}
                  carePlanId={row?.id}
                  renderButton={(onClick) => (
                    <MenuItem onClick={onClick}>
                      <ArticleIcon />
                      {i18n('case.details.generatePrintableCarePlan', 'crs')}
                    </MenuItem>
                  )}
                />,
              ]}
            />
          </TableCell>
        )}
      </TableRow>
  );
}

function ProgressBar({ row, stepLength }: any) {
  const percent = (row / stepLength) * 100;

  return (
    <Stack spacing={1}>
      {stepLength !== undefined && percent !== Infinity && (
        <>
          <LinearProgress variant="determinate" value={percent} color="info" sx={{ mt: 1 }} />
          <Stack
            direction="row"
            justifyContent={
              (percent >= 50 && percent < 100 && 'center') || (percent === 100 && 'right') || 'left'
            }
          >
            <Typography variant="subtitle2">{fPercent(percent) + ` - `}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {row + `/${stepLength}`}
            </Typography>
          </Stack>
        </>
      )}
    </Stack>
  );
}
